import React from "react";
import { HelpItem } from "../Title";

/**
 * Central, plain-language help for each page. Keep entries short — the goal is
 * to orient a user who is not sure what a page or term is responsible for.
 * Used via <Title help={...} helpItems={...}> and <HelpTip>.
 */
export interface PageHelp {
  help: React.ReactNode;
  items: HelpItem[];
}

export const pageHelp: Record<string, PageHelp> = {
  dashboard: {
    help: "A live overview of your testing activity — recent suites, pass/fail results and how busy your agents are right now.",
    items: [
      { term: "Suite", desc: "A group of test cases run together." },
      { term: "Case", desc: "One individual test and its latest result." },
      { term: "Agent", desc: "A machine that actually runs the tests." },
    ],
  },

  cases: {
    help: "Every individual test case the platform knows about, with its most recent result. Each case declares the resources it needs, and the orchestrator runs it on an agent that has them.",
    items: [
      { term: "TC Result", desc: "The test's own verdict — PASSED / FAILED / ABORTED." },
      { term: "Sys Result", desc: "Orchestration state — IN_QUEUE, RESERVED, SUCCESS." },
      { term: "Resources needed", desc: "Devices/emulators the case requires (e.g. a BPM emulator, a phone)." },
    ],
  },

  suites: {
    help: "A suite is a group of test cases launched together. When you create one, the orchestrator queues its cases and routes each to an agent that can run it.",
    items: [
      { term: "Status", desc: "Suite lifecycle: IN_QUEUE → IN_PROGRESS → SUCCESS / FAILED." },
      { term: "Cases", desc: "How many test cases this suite contains." },
      { term: "Create suite", desc: "Add a new suite from a Git repo, uploaded files, or cloud storage." },
    ],
  },

  agents: {
    help: "Agents are the machines that run your tests. Each one advertises the resources it has; the orchestrator only sends a case to an agent that can satisfy what the case needs.",
    items: [
      { term: "Capability badges", desc: "What the agent can run — HIL (real board), SIL (emulator), Mobile, Software." },
      { term: "Running", desc: "Cases the agent is executing or holding right now." },
      { term: "Enable / Disable", desc: "Disabled agents stop picking up new work." },
      { term: "Abort / Requeue", desc: "Stop a stuck case, or put it back in the queue for another run." },
    ],
  },

  artifacts: {
    help: "Files produced by test runs — reports, logs and screenshots — stored in cloud buckets. This page needs a configured cloud storage provider.",
    items: [
      { term: "Bucket", desc: "A cloud storage container holding run outputs." },
      { term: "Report", desc: "The generated result (e.g. Allure HTML) for a case run." },
    ],
  },

  settings: {
    help: "Your account profile and the credentials used to talk to the platform.",
    items: [
      { term: "API token", desc: "Used by agents and CI to authenticate against the API." },
      { term: "Password", desc: "Your sign-in credential for this web app." },
    ],
  },

  management: {
    help: "Admin area for configuring the platform: agents, users, the product catalog, service procedures and component versions.",
    items: [
      { term: "Agents", desc: "Register, approve and inspect executor machines." },
      { term: "Users", desc: "People with access and their roles." },
      { term: "Procedures", desc: "Maintenance actions agents can run on demand." },
    ],
  },

  createSuite: {
    help: "Choose how DeviceSure collects the framework, test cases and resources, then create a suite that the orchestrator will queue and route to a capable agent.",
    items: [
      { term: "Link Repository", desc: "Pull the framework and cases from Git repositories." },
      { term: "Remote", desc: "Create from a remote JSON definition plus uploaded case files." },
      { term: "Artifact Storage", desc: "Pick framework, cases and resources from cloud storage (needs cloud configured)." },
    ],
  },
};
